import { FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styled from "@emotion/styled";

import { atlasRoutes } from "@/app/router/routes";
import { Uuid } from "@/domains/global/identifiers";
import { AccountSchema, DataImportSummarySchema } from "@/domains/openapi/types";
import { DataImportsTable } from "@/pages/data-imports/shared/data-imports-table";
import { api } from "@/modules/api";

const PAGE_SIZE = 50;

export const AccountImportFromMem1Dot0Page: FC = () => {
  const { accountId } = useParams<{ accountId: Uuid }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isStartingImport, setIsStartingImport] = useState(false);

  if (!accountId) {
    return <div>Invalid ID</div>;
  }

  const accountQuery = useQuery({
    queryKey: ["account-by-id", accountId],
    queryFn: () =>
      api.get("/internal/v1/accounts/{account_id}", {
        params: {
          path: {
            account_id: accountId,
          },
        },
      }),
  });

  const spaceAccountId = accountQuery.data?.data?.primary_space_account_id;
  const account: AccountSchema | undefined = accountQuery.data?.data;

  if (accountQuery.isLoading) {
    return <div>Loading account…</div>;
  }

  if (accountQuery.isError) {
    return <div>{`Error loading account: ${String(accountQuery.error)}`}</div>;
  }

  const importsQuery = useQuery({
    queryKey: ["account-data-imports", accountId, spaceAccountId],
    enabled: !!spaceAccountId,
    queryFn: () =>
      api.post("/internal/v1/data-imports/search", {
        body: {
          filter_by_space_account_id: spaceAccountId,
          limit: PAGE_SIZE,
        },
      }),
  });

  const dataImports: DataImportSummarySchema[] = useMemo(
    () => importsQuery.data?.data?.results ?? [],
    [importsQuery.data]
  );

  const isImportRunning = dataImports.some(
    item => item.started_at && !item.ended_at && item.ended_with_error !== true
  );

  const handleStartImport = async () => {
    const confirmation = prompt(
      'Type "CONFIRM" to start importing all notes and collections from Mem 1.0.'
    );

    if (confirmation !== "CONFIRM") {
      alert("Cancelled.");
      return;
    }

    setIsStartingImport(true);

    const response = await api.post("/internal/v1/accounts/{account_id}/import-from-mem-1-dot-0", {
      params: {
        path: {
          account_id: accountId,
        },
      },
      body: {
        config: {
          generate_unique_v2_ids: false,
        },
      },
    });

    if (response.error || !response.data) {
      alert("Failed to start import. Check logs for details.");
      setIsStartingImport(false);
      return;
    }

    await Promise.all([
      importsQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: ["account-by-id", accountId] }),
    ]);
    setIsStartingImport(false);
  };

  const handleRefresh = () => {
    importsQuery.refetch();
  };

  return (
    <Container>
      <Flex justify="between" align="center" mb="3">
        <Heading size="6">Import from Mem 1.0</Heading>
        <Button
          variant="soft"
          onClick={() => navigate(atlasRoutes.accountsView({ params: { accountId } }).path)}
        >
          Back to Account
        </Button>
      </Flex>

      <Card>
        <Flex direction="column" gap="3">
          <Text size="3" weight="bold">
            Account
          </Text>
          <DefinitionList>
            <FragmentedLabel>Account ID</FragmentedLabel>
            <Value>{accountId}</Value>
            <FragmentedLabel>Email</FragmentedLabel>
            <Value>{account?.profile_email_address ?? "—"}</Value>
            <FragmentedLabel>Primary Space Account ID</FragmentedLabel>
            <Value>{spaceAccountId ?? "—"}</Value>
          </DefinitionList>
        </Flex>
      </Card>

      <Section>
        <Heading size="4">How it works</Heading>
        <List>
          <li>
            <b>Only notes and collections created by this account are imported.</b> Items shared by
            others are excluded.
          </li>
          <li>
            <b>Importing may take up to 1 hour.</b> Duration depends on the total number of notes
            and collections.
          </li>
          <li>
            <b>You cannot undo this import.</b> Removing imported content requires manual deletion
            (bulk delete is supported).
          </li>
          <li>
            <b>Content is pulled from the Mem 1.0 account with the same email.</b> Use support if
            you need to import from a different Mem 1.0 account.
          </li>
        </List>
      </Section>

      <Section>
        <Flex justify="between" align="center" mb="2">
          <Heading size="4">Start a new import</Heading>
          <Flex gap="2">
            <Button variant="outline" onClick={handleRefresh} disabled={importsQuery.isLoading}>
              Refresh imports
            </Button>
            <Button
              onClick={handleStartImport}
              disabled={isStartingImport || importsQuery.isLoading}
            >
              {isImportRunning
                ? "Import in progress…"
                : isStartingImport
                  ? "Starting…"
                  : "Import all notes and collections from Mem 1.0"}
            </Button>
          </Flex>
        </Flex>
        <Text size="2" color="gray">
          You&apos;ll see the import appear below once it starts.
        </Text>
      </Section>

      <Section>
        <Flex justify="between" align="center" mb="2">
          <Heading size="4">Your imports</Heading>
          {importsQuery.isFetching && <Text size="2">Updating…</Text>}
        </Flex>
        {importsQuery.isLoading ? (
          <div>Loading imports…</div>
        ) : importsQuery.isError ? (
          <ErrorText>{`Error fetching imports: ${String(importsQuery.error)}`}</ErrorText>
        ) : dataImports.length > 0 ? (
          <DataImportsTable
            data={dataImports}
            onViewDataImport={id =>
              navigate(atlasRoutes.dataImportsView({ params: { dataImportId: id } }).path)
            }
            pageSize={PAGE_SIZE}
          />
        ) : (
          <Text size="2" color="gray">
            No imports found for this account yet.
          </Text>
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
`;

const DefinitionList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  row-gap: 6px;
  column-gap: 12px;
  margin: 0;
`;

const FragmentedLabel = styled.dt`
  font-weight: 600;
  color: var(--gray-12);
`;

const Value = styled.dd`
  margin: 0;
  word-break: break-all;
`;

const ErrorText = styled(Text)`
  color: #b3261e;
`;
